import { useModalControls } from "@hostingbv/lib/src/components/layout/ModalProvider";
import { FC, useCallback, useEffect, useState } from "react";
import { config } from "../config";
import { useApiClient } from "@hostingbv/lib/src/api";
import { Autocomplete } from "@hostingbv/lib/src/components/form/Autocomplete";
import { Button, ObjectAutoSelect } from "@ventumsoftware/react-toolkit";
import { useNavigate } from "react-router-dom";
import { useFetchedResource } from "../../../lib/src/util/useFetchedResource";
import { useRefresh } from "../../../lib/src/components/RefreshController";
import { Domain } from "../../../lib/src/api/partner/models/Domain";
import { useRealtimeDeployments } from "../components/deployments/RealtimeDeployments";
import { Loading } from "../../../lib/src/components/Loading";
import { Customer } from "@hostingbv/lib/src/api/common/models/Customer";
import { PillSelect } from "../../../lib/src/components/form/PillSelect";
import { MailType } from "@hostingbv/lib/src/api/partner/models/MailDomain";


export const AddMailDomainModal: FC = () => {
  const { partnerClient } = useApiClient(config)
  const { awaitNextDeploymentFinished } = useRealtimeDeployments()!
  const navigate = useNavigate()
  const refresh = useRefresh()
  const { close } = useModalControls()

  const [mailType, setMailType] = useState<MailType>('imap_mail_domain')
  const [isSaving, setIsSaving] = useState(false)

  const { resource: customers, loading: loadingCustomers, reload: reloadCustomers } = useFetchedResource(() => partnerClient.getAllCustomers())
  const { resource: mailDomains, loading: loadingMailDomains, reload: reloadMailDomains } = useFetchedResource(() => partnerClient.getAllMailDomains())
  const { resource: domains, loading: loadingDomains, reload: reloadDomains } = useFetchedResource(() => partnerClient.getAllDomains())

  useEffect(() => {
    reloadCustomers()
    reloadMailDomains()
    reloadDomains()
  }, [])

  const [domain, setDomain] = useState<Domain>()
  const [transactionalDomain, setTransactionalDomain] = useState('')
  const [customer, setCustomer] = useState<Customer>()
  const [error, setError] = useState('')

  const finish = useCallback(() => {
    refresh()
    close()
    navigate(`/mail`)
  }, [])

  const save = useCallback(async () => {
    const isTransactional = mailType === 'transactional_mail_domain'
    const domainName = isTransactional ? transactionalDomain.trim() : domain?.displayName
    if (!domainName) {
      setError(isTransactional ? 'Please enter a domain' : 'Please select a domain')
      return
    }
    if (customer === undefined) {
      setError('Please select a customer')
      return
    }
    setIsSaving(true)
    try {
      const result = isTransactional
        ? await partnerClient.createTransactionalMailDomain(domainName, customer.id)
        : await partnerClient.createMailDomain(domainName, customer.id)
      if (!result.success) {
        setError(result.error)
        return
      }
      if (result.data.entityId) {
        await awaitNextDeploymentFinished(result.data.entityId)
      }
      finish()
    } finally {
      setIsSaving(false)
    }
  }, [domain, transactionalDomain, customer, mailType])

  const domainsData = domains?.success ? domains.data : []
  const mailDomainsData = mailDomains?.success ? mailDomains.data : []
  const customersData = customers?.success ? customers.data : []

  const availableDomains = domainsData.filter(domain => !mailDomainsData.find(mailDomain => mailDomain.displayName === domain.displayName)) || []

  useEffect(() => {
    if (customer === undefined && customersData.length > 0) {
      setCustomer(customersData[0])
    }
  }, [customersData])

  useEffect(() => {
    if (domain === undefined && availableDomains.length > 0) {
      setDomain(availableDomains[0])
    }
  }, [availableDomains])

  return <>
    <form onSubmit={(e) => { e.preventDefault(); save() }} className={"space-y-4 flex-1"}>
      <PillSelect value={mailType} onChange={value => setMailType(value as MailType)} options={{ imap_mail_domain: 'IMAP', transactional_mail_domain: 'Transactional' }} />
      <Loading loading={loadingCustomers || loadingDomains || loadingMailDomains}>
        {customersData.length < 1 ? <div className={"font-medium text-red-600"}>
          No available customers
        </div> : <>
          <ObjectAutoSelect label={'Customer'} options={customersData} value={customer ?? null} onChange={setCustomer} displayItem={d => d.displayName} idProperty={'id'} />
        </>}
        {mailType === 'transactional_mail_domain' ? <>
          <Autocomplete allowCustomValue label={'Domain'} placeholder={'mail.example.com'} options={Object.fromEntries(availableDomains.map(d => [d.displayName, d.displayName]))} value={transactionalDomain} onChange={setTransactionalDomain} />
        </> : availableDomains.length < 1 ? <div className={"font-medium text-red-600"}>
          No available domains
        </div> : <>
          <ObjectAutoSelect label={'Domain'} options={availableDomains} value={domain ?? null} onChange={setDomain} displayItem={d => d.displayName} idProperty={'id'} />
        </>}
        {error.length > 0 && <div className={"font-medium text-red-600"}>
          {error}
        </div>}
      </Loading>
    </form>
    <footer className={"-mx-4 px-4 mt-3 py-3 border-t border-slate-100 flex items-center space-x-4"}>
      <Button type={'primary'} disabled={isSaving} size={'md'} text={isSaving ? 'Deploying...' : 'Deploy'} onClick={save} />
      <Button type={'secondary'} size={'md'} text={'Cancel'} onClick={() => close()} />
    </footer>
  </>
}
